import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { MAX_FILE_SIZE, useUploadDocument, type DocumentRow } from '@/hooks/useDocuments';
import { resolveFileMimeType } from '@/utils/fileMime';
import { countAttachmentsByEntryId } from '@/utils/journalHistoryFilters';

export interface JournalAttachmentRow {
  id: string;
  clinic_id: string;
  journal_entry_id: string;
  document_id: string;
  created_by: string | null;
  created_at: string;
  document: DocumentRow;
}

export const JOURNAL_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
] as const;

export function resolveJournalFileMimeType(file: File): string {
  return resolveFileMimeType(file);
}

export function validateJournalAttachmentFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) return 'size_limit_exceeded';
  const mime = resolveJournalFileMimeType(file);
  if (!JOURNAL_ALLOWED_MIME_TYPES.includes(mime as (typeof JOURNAL_ALLOWED_MIME_TYPES)[number])) {
    return 'invalid_type';
  }
  return null;
}

export const journalAttachmentsKey = (entryId: string | null | undefined) =>
  ['journal', 'attachments', entryId ?? null] as const;

export const journalAttachmentCountsKey = (
  clinicId: string | null | undefined,
  entryIds: string[],
) => ['journal', 'attachment-counts', clinicId ?? null, ...[...entryIds].sort()] as const;

export function useJournalAttachmentCounts(
  clinicId: string | null | undefined,
  entryIds: string[],
) {
  const sortedIds = [...entryIds].sort();
  return useQuery({
    queryKey: journalAttachmentCountsKey(clinicId, sortedIds),
    enabled: Boolean(clinicId) && sortedIds.length > 0,
    queryFn: async (): Promise<Record<string, number>> => {
      if (!clinicId || sortedIds.length === 0) return {};
      const { data, error } = await supabase
        .from('journal_entry_attachments')
        .select('journal_entry_id')
        .eq('clinic_id', clinicId)
        .in('journal_entry_id', sortedIds);
      if (error) throw error;
      return countAttachmentsByEntryId(data ?? []);
    },
    staleTime: 10_000,
  });
}

export function useJournalEntryAttachments(entryId: string | null | undefined) {
  return useQuery({
    queryKey: journalAttachmentsKey(entryId),
    enabled: Boolean(entryId),
    queryFn: async (): Promise<JournalAttachmentRow[]> => {
      if (!entryId) return [];
      const { data, error } = await supabase
        .from('journal_entry_attachments')
        .select(
          'id, clinic_id, journal_entry_id, document_id, created_by, created_at, document:documents(*)',
        )
        .eq('journal_entry_id', entryId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      return (
        (data ?? []) as unknown as Array<
          Omit<JournalAttachmentRow, 'document'> & { document: DocumentRow }
        >
      ).map((row) => ({
        id: row.id,
        clinic_id: row.clinic_id,
        journal_entry_id: row.journal_entry_id,
        document_id: row.document_id,
        created_by: row.created_by,
        created_at: row.created_at,
        document: row.document,
      }));
    },
    staleTime: 10_000,
  });
}

export function useAttachJournalEntryDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { journal_entry_id: string; document_id: string }) => {
      const { data, error } = await supabase.rpc('attach_journal_entry_document', {
        p_journal_entry_id: input.journal_entry_id,
        p_document_id: input.document_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: journalAttachmentsKey(input.journal_entry_id) });
      qc.invalidateQueries({ queryKey: ['journal', 'attachment-counts'] });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents', 'patient'] });
      }
    },
  });
}

export function useDetachJournalEntryDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { attachment_id: string; journal_entry_id: string }) => {
      const { data, error } = await supabase.rpc('detach_journal_entry_document', {
        p_attachment_id: input.attachment_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: journalAttachmentsKey(input.journal_entry_id) });
      qc.invalidateQueries({ queryKey: ['journal', 'attachment-counts'] });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents'] });
      }
    },
  });
}

export function useUploadJournalAttachment(clinicId: string | null | undefined) {
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachJournalEntryDocument(clinicId);

  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      journalEntryId: string;
      file: File;
    }): Promise<DocumentRow> => {
      const doc = await uploadDocument.mutateAsync({
        clinicId: input.clinicId,
        patientId: input.patientId,
        file: input.file,
        tags: ['journal'],
      });
      await attachDocument.mutateAsync({
        journal_entry_id: input.journalEntryId,
        document_id: doc.id,
      });
      return doc;
    },
  });
}

export interface JournalAmendmentAttachmentRow {
  id: string;
  clinic_id: string;
  amendment_id: string;
  document_id: string;
  created_by: string | null;
  created_at: string;
  document: DocumentRow;
}

export const journalAmendmentAttachmentsKey = (amendmentId: string | null | undefined) =>
  ['journal', 'amendment-attachments', amendmentId ?? null] as const;

export function useJournalAmendmentAttachments(amendmentId: string | null | undefined) {
  return useQuery({
    queryKey: journalAmendmentAttachmentsKey(amendmentId),
    enabled: Boolean(amendmentId),
    queryFn: async (): Promise<JournalAmendmentAttachmentRow[]> => {
      if (!amendmentId) return [];
      const { data, error } = await supabase
        .from('journal_amendment_attachments')
        .select(
          'id, clinic_id, amendment_id, document_id, created_by, created_at, document:documents(*)',
        )
        .eq('amendment_id', amendmentId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      return (
        (data ?? []) as unknown as Array<
          Omit<JournalAmendmentAttachmentRow, 'document'> & { document: DocumentRow }
        >
      ).map((row) => ({
        id: row.id,
        clinic_id: row.clinic_id,
        amendment_id: row.amendment_id,
        document_id: row.document_id,
        created_by: row.created_by,
        created_at: row.created_at,
        document: row.document,
      }));
    },
    staleTime: 10_000,
  });
}

export function useAttachJournalAmendmentDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { amendment_id: string; document_id: string }) => {
      const { data, error } = await supabase.rpc('attach_journal_amendment_document', {
        p_amendment_id: input.amendment_id,
        p_document_id: input.document_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: journalAmendmentAttachmentsKey(input.amendment_id) });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents', 'patient'] });
      }
    },
  });
}

export function useDetachJournalAmendmentDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { attachment_id: string; amendment_id: string }) => {
      const { data, error } = await supabase.rpc('detach_journal_amendment_document', {
        p_attachment_id: input.attachment_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: journalAmendmentAttachmentsKey(input.amendment_id) });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents'] });
      }
    },
  });
}

export function useUploadJournalAmendmentAttachment(clinicId: string | null | undefined) {
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachJournalAmendmentDocument(clinicId);

  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      amendmentId: string;
      file: File;
    }): Promise<DocumentRow> => {
      const doc = await uploadDocument.mutateAsync({
        clinicId: input.clinicId,
        patientId: input.patientId,
        file: input.file,
        tags: ['journal'],
      });
      await attachDocument.mutateAsync({
        amendment_id: input.amendmentId,
        document_id: doc.id,
      });
      return doc;
    },
  });
}
